import React from 'react';
import { useTheme } from '../context/ThemeContext';

const TermsPage = () => {
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen pt-24 md:pt-32 pb-16 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Terms of Service
          </h1>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-white/50' : 'text-gray-500'
          }`}>
            Last updated: January 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          {/* Intro */}
          <p className={`text-base leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            Welcome to RKS Technologies. By accessing or using our Website and services, you agree to be bound by these Terms of Service ("Terms").
          </p>

          {/* Section 1 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>1. Use of the Website</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              You agree to use this Website only for lawful purposes and in a way that does not infringe on the rights of others or restrict their use of the site.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>2. Services</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              RKS Technologies provides IT talent sourcing, consulting, and related services. Specific service terms may be outlined in separate agreements or proposals.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>3. Intellectual Property</h2>
            <p className={`mb-4 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              All content on this Website, including text, logos, graphics, and design, is the property of RKS Technologies unless otherwise stated.
            </p>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              You may not:
            </p>
            <ul className={`list-disc pl-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Copy, reproduce, or distribute content without written permission</li>
              <li>Use our branding without authorization</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>4. User Submissions</h2>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              By submitting information (resumes, forms, inquiries), you:
            </p>
            <ul className={`list-disc pl-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Confirm the information is accurate</li>
              <li>Grant us permission to use it for service delivery and communication</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>5. Limitation of Liability</h2>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              To the fullest extent permitted by law:
            </p>
            <ul className={`list-disc pl-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>RKS Technologies shall not be liable for any indirect, incidental, or consequential damages</li>
              <li>Use of the Website and services is at your own risk</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>6. Disclaimer</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              Our Website and services are provided "as is" and "as available" without warranties of any kind, express or implied.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>7. Termination</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We reserve the right to suspend or terminate access to our Website or services if these Terms are violated.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>8. Governing Law</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              These Terms shall be governed by and interpreted in accordance with the applicable laws where RKS Technologies operates, without regard to conflict-of-law principles.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>9. Changes to Terms</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may update these Terms at any time. Continued use of the Website after changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>10. Contact Information</h2>
            <p className={`mb-4 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              For questions regarding these Terms, contact us at:
            </p>
            <div className={`p-6 ${
              theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-gray-100 border border-gray-200'
            }`}>
              <p className={`mb-2 ${
                theme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Email:</strong>{' '}
                <a 
                  href="mailto:info@rkstechnologiespa.com"
                  className={theme === 'dark' ? 'text-[#00FFD1] hover:underline' : 'text-[#00997A] hover:underline'}
                >
                  info@rkstechnologiespa.com
                </a>
              </p>
              <p className={`${
                theme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Website:</strong>{' '}
                <a 
                  href="https://rkstechnologiespa.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={theme === 'dark' ? 'text-[#00FFD1] hover:underline' : 'text-[#00997A] hover:underline'}
                >
                  https://rkstechnologiespa.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
